import React from "react";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonStatusBadge from "Components/Common/CommonStatusBadge";
import { formatDate } from "common/service/CommonService";
import { formatTime24Hour } from "modules/Session/Session/helper/sessionlogconstant";
import ip from "modules/Pharmacy/IPinventory/css/ipInventory.module.scss";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";

interface SessionsTabProps {
  data: any[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onResume: (row: any) => void;
  onViewLog: (sessionid: string | number) => void;
  showDosingStation?: boolean;
  showSubjects?: boolean;
  showVacutainers?: boolean;
  showAliquots?: boolean;
}

const SessionsTab: React.FC<SessionsTabProps> = ({
  data,
  loading,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
  onResume,
  onViewLog,
  showDosingStation = true,
  showSubjects = true,
  showVacutainers = true,
  showAliquots = true,
}) => {
  const allColumns = [
    {
      key: "sessionid",
      label: "Session ID",
      width: "150px",
      sticky: true,
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>{row?.sessionno || row?.sessionid || "-"}</p>
      ),
    },
    {
      key: "studycode",
      label: "Study Code",
      width: "120px",
      sticky: true,
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>{row?.studycode || "-"}</p>
      ),
    },
    {
      key: "period_timepoint",
      label: "Period & Timepoint",
      width: "180px",
      sticky: true,
      divider: true,
      textCenter: true,
      render: (_: any, row?: any) => (
        <div className="d-flex justify-content-center align-items-center gap-2">
          <span className="fw-semibold text-dark">
            P - {row?.period_number || "-"}
          </span>
          <span className="fw-semibold text-dark">|</span>
          <span className="fw-semibold text-dark">
            T - {`(${row?.timepoint_number || "-"})`}
          </span>
        </div>
      ),
    },
    {
      key: "scanningmode",
      label: "Dosing Station",
      render: (_: any, row?: any) => (
        <div className="d-flex justify-content-center align-items-center">
          <span
            className="badge bg-primary-subtle text-primary border border-primary border-opacity-25"
            style={{ fontSize: "11px", padding: "5px 10px", borderRadius: "6px" }}
          >
            <i className="ri-building-2-line me-1"></i>
            {`DS-${row?.dosingstationno ? String(row.dosingstationno).padStart(2, "0") : "-"}`}
          </span>
        </div>
      ),
    },
    {
      key: "subjects",
      label: "Subjects",
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>
          {`${row?.completed_subjects || 0} / ${row?.total_subjects || 0}`}
        </p>
      ),
    },
    {
      key: "vacutainers",
      label: "Vacutainers",
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>
          {`${row?.completed_vaccutainers || 0} / ${row?.total_vaccutainers || 0}`}
        </p>
      ),
    },
    {
      key: "aliquots",
      label: "Aliquots",
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>
          {`${row?.completed_aliquots || 0} / ${row?.total_aliquots || 0}`}
        </p>
      ),
    },
    {
      key: "status",
      label: "Status",
      textCenter: true,
      render: (_: any, row?: any) => {
        const status = row?.display_status || "PAUSED";
        let color = "secondary";
        if (status === "COMPLETED") color = "success";
        else if (status === "ACTIVE") color = "primary";
        else if (status === "PAUSED") color = "warning";
        else if (status === "ATTENTION") color = "danger";

        return (
          <div className="d-flex justify-content-center">
            <CommonStatusBadge
              isActive={true}
              activeText={status}
              activeColor={color}
              style={{ width: "85px" }}
            />
          </div>
        );
      },
    },
    {
      key: "lastactive",
      label: "Last Active",
      render: (_: any, row?: any) => (
        <p className={ip.cellPrimary}>
          {row?.last_active
            ? `${formatDate(row.last_active)} (${formatTime24Hour(row.last_active)})`
            : "-"}
        </p>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (_: any, row?: any) => {
        const isCompleted = row?.display_status === "COMPLETED";
        const actionLabel = isCompleted ? "View Log" : "Resume";
        const actionPermission = isCompleted
          ? `${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT},${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER},${PERMISSION_ACTIONS.AUDIT}`
          : `${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT},${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}`;

        return (
          <CommonButton
            module={PERMISSION_MODULES.SESSION}
            action={actionPermission}
            color="primary"
            outline
            className="rounded-pill fw-semibold d-flex align-items-center justify-content-center gap-1"
            style={{
              width: "100px",
              padding: "2px 10px",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              if (isCompleted) {
                onViewLog(row?.sessionid || "1");
              } else {
                onResume(row || {});
              }
            }}
          >
            <i className={`${isCompleted ? "ri-file-list-3-line" : "ri-play-line"} fs-13`} />
            {actionLabel}
          </CommonButton>
        );
      },
    },
  ];

  const columns = allColumns.filter((col) => {
    if (col.key === "scanningmode" && !showDosingStation) return false;
    if (col.key === "subjects" && !showSubjects) return false;
    if (col.key === "vacutainers" && !showVacutainers) return false;
    if (col.key === "aliquots" && !showAliquots) return false;
    return true;
  });

  return (
    <CommonTableIndexWise
      columns={columns}
      data={data}
      loading={loading}
      pageSize={pageSize}
      currentPage={currentPage}
      totalRecords={totalRecords}
      onPageChange={onPageChange}
      actions={{}}
    />
  );
};

export default SessionsTab;
